import { Alert, Box, Radio, Skeleton, Typography, useTheme } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    KboFlowDebugService,
    KboFlowStepDebugExecuteResult,
    KboFlowStepDebugRun,
} from "services/KboFlowDebugService";
import {
    KboFlowStepDebugService,
    KboFlowStepDebugSkill,
} from "services/KboFlowStepDebugService";
import { UserForSharing, UserService } from "services/UserService";
import {
    KboFlowRunDebug,
    KboFlowRunOutput,
} from "../KboFlowDebug/KboFlowRunDebug";
import { shortId } from "../KboFlowDebug/KboFlowDebugUI";
import { formatRunDate } from "../KboFlowRuns/KboFlowRunDigestModels";
import OrganizationBreadcrumbs, {
    BreadcrumbType,
} from "../OrganizationBreadcrumbs";
import {
    DEFAULT_FLOW_STEP_ID,
    findFlowStep,
    FLOW_STEPS,
    FlowStep,
} from "./FlowStepCatalog";
import {
    DEFAULT_MANUAL_INPUT,
    FlowStepDebugManualInput,
    isManualInputComplete,
    ManualInput,
} from "./FlowStepDebugManualInput";
import { FlowStepDebugNodePicker } from "./FlowStepDebugNodePicker";
import {
    DEFAULT_RUN_PICKER_FILTERS,
    FlowStepDebugRunPicker,
    RunPickerFilters,
    subjectUserLabel,
} from "./FlowStepDebugRunPicker";
import { FlowStepDebugEvaluation } from "./FlowStepDebugEvaluation";
import { FlowStepDebugSkills, SkillDrafts } from "./FlowStepDebugSkills";
import {
    FlowStepDebugStage,
    SectionCard,
    SectionCardFooter,
    SectionCardHeader,
    StepCardLabel,
    WizardTabs,
} from "./FlowStepDebugUI";
import { FlowStepSelect } from "./FlowStepSelect";

type InputSource = "run" | "manual";

export type FlowStepDebugLocationState = {
    /** Persisted KboFlowRuns row id selected from the Runs page. */
    sourceRunId: string;
    /** Optional debug target supplied by a contextual action on the run detail. */
    stepId?: string;
    /** Exploration node required by the Analyze Node step. */
    nodeId?: string;
    /** Result title that led to the contextual Analyze Node handoff. */
    resultTitle?: string;
};

function locationStateFromLocation(
    state: unknown,
): FlowStepDebugLocationState | undefined {
    if (!state || typeof state !== "object") return undefined;
    const locationState = state as Partial<FlowStepDebugLocationState>;
    if (
        typeof locationState.sourceRunId !== "string" ||
        !locationState.sourceRunId
    ) {
        return undefined;
    }
    return {
        sourceRunId: locationState.sourceRunId,
        stepId:
            typeof locationState.stepId === "string"
                ? locationState.stepId
                : undefined,
        nodeId:
            typeof locationState.nodeId === "string"
                ? locationState.nodeId
                : undefined,
        resultTitle:
            typeof locationState.resultTitle === "string"
                ? locationState.resultTitle
                : undefined,
    };
}

function firstQuestionText(output: KboFlowRunOutput): string | undefined {
    return [
        output.pursuedQuestions,
        output.validatedQuestions,
        output.generatedQuestions,
    ]
        .flatMap((questions) => questions ?? [])
        .map((question) => question.text?.trim())
        .find((text): text is string => !!text);
}

function toStepDebugRun(
    run: KboFlowRunDebug,
    output: KboFlowRunOutput,
): KboFlowStepDebugRun {
    return {
        id: run.id,
        subjectUserId: run.subjectUserId,
        runId: output.runId,
        createdOn: run.createdOn,
        startedAtUtc: output.startedAtUtc,
        completedAtUtc: output.completedAtUtc,
        question: firstQuestionText(output),
        questionCount: output.generatedQuestions?.length ?? 0,
        stepCount: output.phaseRecords?.length ?? 0,
    };
}

const INPUT_SOURCES: {
    id: InputSource;
    label: string;
    description: string;
}[] = [
    {
        id: "run",
        label: "Completed database run",
        description:
            "Replays the exact input this step received in production.",
    },
    {
        id: "manual",
        label: "Manual input",
        description:
            "Enter the step payload yourself. No run to compare against.",
    },
];

/** One of the bordered radio cards in the "input source" card. */
function InputSourceOption({
    option,
    selected,
    onSelect,
}: {
    option: typeof INPUT_SOURCES[number];
    selected: boolean;
    onSelect: () => void;
}) {
    const theme = useTheme();
    return (
        <Box
            role="radio"
            aria-checked={selected}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect();
                }
            }}
            sx={{
                bgcolor: selected ? "primary.50" : "transparent",
                border: "1px solid",
                borderColor: selected
                    ? theme.palette.primary.main
                    : theme.palette.gray[200],
                borderRadius: 2,
                cursor: "pointer",
                display: "flex",
                gap: 1,
                p: 1.75,
            }}
        >
            <Radio
                checked={selected}
                size="small"
                tabIndex={-1}
                sx={{ mt: -0.75 }}
                inputProps={{ "aria-label": option.label }}
            />
            <Box>
                <Typography sx={{ fontWeight: 700 }}>{option.label}</Typography>
                <Typography
                    variant="body2"
                    sx={{ color: theme.palette.gray[600], mt: 0.25 }}
                >
                    {option.description}
                </Typography>
            </Box>
        </Box>
    );
}

function SelectedRunSummary({
    run,
    usersById,
}: {
    run?: KboFlowStepDebugRun;
    usersById: Map<string, UserForSharing>;
}) {
    return (
        <>
            <SectionCardHeader
                title="3 · Selected run"
                subtitle="Using the run you opened Flow Step Debug from."
            />
            <Box sx={{ px: 2.5, py: 2 }}>
                {run ? (
                    <>
                        <Typography sx={{ fontWeight: 700 }}>
                            {run.question
                                ? `“${run.question}”`
                                : "No question recorded"}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Run {shortId(run.runId ?? run.id)} ·{" "}
                            {subjectUserLabel(run.subjectUserId, usersById)} ·{" "}
                            {formatRunDate(run.startedAtUtc ?? run.createdOn)}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Skeleton width="65%" height={28} />
                        <Skeleton width="40%" height={22} />
                    </>
                )}
            </Box>
        </>
    );
}

const FlowStepDebug: React.FC<{ orgId: string }> = ({ orgId }) => {
    const theme = useTheme();
    const location = useLocation();
    const launchedState = locationStateFromLocation(location.state);
    const launchedRunId = launchedState?.sourceRunId;
    const launchedStep = findFlowStep(launchedState?.stepId);
    const service = useMemo(() => new KboFlowDebugService(), []);
    const userService = useMemo(() => new UserService(), []);
    // Skills come from the core database, so they are read through their own controller.
    const stepDebugService = useMemo(() => new KboFlowStepDebugService(), []);

    const [stage, setStage] = useState<FlowStepDebugStage>("Configure");
    const [step, setStep] = useState<FlowStep>(
        () =>
            launchedStep ??
            findFlowStep(
                launchedRunId ? "analyze-one-node" : DEFAULT_FLOW_STEP_ID,
            ) ??
            FLOW_STEPS[0],
    );
    const [inputSource, setInputSource] = useState<InputSource>("run");
    const [filters, setFilters] = useState<RunPickerFilters>(() =>
        launchedRunId
            ? { ...DEFAULT_RUN_PICKER_FILTERS, days: 0 }
            : DEFAULT_RUN_PICKER_FILTERS,
    );
    const [selectedRun, setSelectedRun] = useState<KboFlowStepDebugRun>();
    const [launchedRunOutput, setLaunchedRunOutput] =
        useState<KboFlowRunOutput>();
    const [launchError, setLaunchError] = useState<string>();
    // Only meaningful for "analyze-one-node" — which node in the run's exploration tree to test.
    const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>(
        () => launchedState?.nodeId,
    );
    const [manualInput, setManualInput] =
        useState<ManualInput>(DEFAULT_MANUAL_INPUT);
    const [users, setUsers] = useState<UserForSharing[]>([]);
    const [skills, setSkills] = useState<KboFlowStepDebugSkill[]>([]);
    const [skillsLoading, setSkillsLoading] = useState(true);
    const [skillsError, setSkillsError] = useState<string>();
    const [selectedSkillId, setSelectedSkillId] = useState<string>();
    const [skillDrafts, setSkillDrafts] = useState<SkillDrafts>({});
    // Owned here so the Evaluation stage judges the same result Skill Instructions produced.
    const [stepResult, setStepResult] =
        useState<KboFlowStepDebugExecuteResult>();
    // Bumped only by the Evaluate button, so re-opening the tab never spends another judge call.
    const [judgeToken, setJudgeToken] = useState(0);

    useEffect(() => {
        if (!launchedRunId) return undefined;
        const controller = new AbortController();
        setLaunchError(undefined);
        service
            .getRun(launchedRunId, controller.signal)
            .then((run) => {
                const output =
                    typeof run.runOutput === "object" && run.runOutput
                        ? run.runOutput
                        : undefined;
                if (output) {
                    setSelectedRun(toStepDebugRun(run, output));
                    setLaunchedRunOutput(output);
                } else {
                    setLaunchError(
                        "The selected run does not contain usable debug data.",
                    );
                }
            })
            .catch((caught: unknown) => {
                if (controller.signal.aborted) return;
                setLaunchError(
                    caught instanceof Error
                        ? caught.message
                        : "The selected run could not be loaded.",
                );
            });
        return () => controller.abort();
    }, [launchedRunId, service]);

    useEffect(() => {
        if (!orgId) return undefined;
        let cancelled = false;
        userService
            .getUsersForSharing(orgId)
            .then((result) => {
                if (!cancelled) setUsers(result);
            })
            .catch(() => {
                if (!cancelled) setUsers([]);
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, userService]);

    const usersById = useMemo(
        () => new Map(users.map((user) => [user.userId.toLowerCase(), user])),
        [users],
    );

    const launchedRunBreadcrumbs = useMemo<BreadcrumbType[] | undefined>(() => {
        if (!launchedRunId) return undefined;
        const runLink = `/admin/organizations/${orgId}/kbo-flow-runs/${launchedRunId}`;
        return [
            {
                label: "Flow runs",
                link: `/admin/organizations/${orgId}/kbo-flow-runs`,
            },
            {
                label: selectedRun
                    ? formatRunDate(
                          selectedRun.startedAtUtc ?? selectedRun.createdOn,
                      )
                    : shortId(launchedRunId),
                link: runLink,
            },
            { label: "Flow Step Debug" },
        ];
    }, [launchedRunId, orgId, selectedRun]);

    // The KboFlow-scoped skills are the same for every step, so they are fetched once here
    // rather than per stage visit.
    useEffect(() => {
        const controller = new AbortController();
        setSkillsLoading(true);
        setSkillsError(undefined);
        stepDebugService
            .getSkills(controller.signal)
            .then((result) => setSkills(result ?? []))
            .catch((caught: unknown) => {
                if (controller.signal.aborted) return;
                setSkills([]);
                setSkillsError(
                    caught instanceof Error
                        ? caught.message
                        : "KboFlow skills could not be loaded.",
                );
            })
            .finally(() => {
                if (!controller.signal.aborted) setSkillsLoading(false);
            });
        return () => controller.abort();
    }, [stepDebugService]);

    // Nothing in the database ties a skill to a step; the catalog carries that mapping,
    // transcribed from the KboFlow workflow and its agent factory.
    // Ordered as the step's agents load them, not alphabetically, so the chips read in the
    // order the step actually runs through.
    const stepSkills = useMemo(() => {
        const byName = new Map(
            skills
                .filter((skill) => skill.name)
                .map((skill) => [skill.name!.toLowerCase(), skill]),
        );
        return step.skillNames
            .map((name) => byName.get(name.toLowerCase()))
            .filter((skill): skill is KboFlowStepDebugSkill => !!skill);
    }, [skills, step.skillNames]);

    // Keep a skill selected whenever the step offers any, including after a step change drops
    // the previous one.
    useEffect(() => {
        if (stepSkills.length === 0) {
            setSelectedSkillId(undefined);
            return;
        }
        setSelectedSkillId((current) =>
            current && stepSkills.some((skill) => skill.id === current)
                ? current
                : stepSkills[0].id,
        );
    }, [stepSkills]);

    // A contextually launched run remains the debug target when changing steps. A run chosen from the
    // general picker is cleared because its eligibility was based on the previous step.
    const handleStepChange = useCallback(
        (next: FlowStep) => {
            setStep(next);
            if (selectedRun?.id !== launchedRunId) {
                setSelectedRun(undefined);
                setLaunchedRunOutput(undefined);
            }
            setSelectedNodeId(
                next.id === "analyze-one-node"
                    ? launchedState?.nodeId
                    : undefined,
            );
            setStepResult(undefined);
            setStage("Configure");
        },
        [launchedRunId, launchedState?.nodeId, selectedRun?.id],
    );

    // A new run has its own tree, so any node picked from the previous one is stale.
    const handleSelectRun = useCallback(
        (run: KboFlowStepDebugRun | undefined) => {
            setSelectedRun(run);
            setLaunchedRunOutput(undefined);
            setSelectedNodeId(undefined);
        },
        [],
    );

    const requiresNode = step.id === "analyze-one-node";
    const configured =
        inputSource === "run"
            ? selectedRun !== undefined &&
              (!requiresNode || selectedNodeId !== undefined)
            : isManualInputComplete(manualInput);

    const reachableStages: FlowStepDebugStage[] = configured
        ? ["Configure", "Skill Instructions", "Evaluation"]
        : ["Configure"];

    const selectionSummary = (
        <>
            Selected:{" "}
            <Box
                component="span"
                sx={{ color: "text.primary", fontWeight: 700 }}
            >
                {step.label}
            </Box>
            {inputSource === "manual"
                ? " · manual input"
                : selectedRun
                ? ` · run ${shortId(
                      selectedRun.runId ?? selectedRun.id,
                  )} · ${subjectUserLabel(
                      selectedRun.subjectUserId,
                      usersById,
                  )} · ${formatRunDate(
                      selectedRun.startedAtUtc ?? selectedRun.createdOn,
                  )}${
                      requiresNode
                          ? selectedNodeId
                              ? ` · node ${shortId(selectedNodeId)}`
                              : " · no node picked yet"
                          : ""
                  }`
                : " · no run picked yet"}
        </>
    );

    return (
        <Box
            sx={{
                bgcolor: theme.palette.gray[50],
                minHeight: "100%",
                p: { xs: 2, md: 4 },
                width: "100%",
            }}
        >
            <Box sx={{ maxWidth: 1440, mx: "auto" }}>
                <OrganizationBreadcrumbs
                    pageTitle={launchedRunId ? undefined : "Flow Step Debug"}
                    sectionLabel="KBo"
                    additionalBreadcrumbs={launchedRunBreadcrumbs}
                />

                <Box sx={{ mb: 3, mt: 2.5 }}>
                    <Typography
                        component="h1"
                        sx={{
                            color: theme.palette.gray[800],
                            fontSize: { xs: 28, md: 34 },
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                        }}
                    >
                        Flow Step Debug
                    </Typography>
                    <Typography
                        color="text.secondary"
                        sx={{ maxWidth: 620, mt: 0.5 }}
                    >
                        Execute a single production step against a test input
                        and inspect what it produced. Nothing you do here
                        affects the original run or the live flow.
                    </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <WizardTabs
                        activeStage={stage}
                        reachableStages={reachableStages}
                        onStageChange={setStage}
                    />
                </Box>

                {stage === "Skill Instructions" ? (
                    <FlowStepDebugSkills
                        step={step}
                        skills={stepSkills}
                        loading={skillsLoading}
                        error={skillsError}
                        selectedSkillId={selectedSkillId}
                        onSelectSkill={setSelectedSkillId}
                        drafts={skillDrafts}
                        onDraftsChange={setSkillDrafts}
                        service={service}
                        sourceRunId={
                            inputSource === "run" ? selectedRun?.id : undefined
                        }
                        nodeId={
                            inputSource === "run" && requiresNode
                                ? selectedNodeId
                                : undefined
                        }
                        result={stepResult}
                        onResultChange={setStepResult}
                        onEvaluate={() => {
                            setJudgeToken((token) => token + 1);
                            setStage("Evaluation");
                        }}
                    />
                ) : stage === "Evaluation" ? (
                    <FlowStepDebugEvaluation
                        step={step}
                        skills={stepSkills}
                        drafts={skillDrafts}
                        result={stepResult}
                        service={service}
                        sourceRunId={
                            inputSource === "run" ? selectedRun?.id : undefined
                        }
                        judgeToken={judgeToken}
                        onTryAnotherRun={() => setStage("Skill Instructions")}
                    />
                ) : (
                    <Box
                        sx={{
                            alignItems: "flex-start",
                            display: "grid",
                            gap: 3,
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "minmax(280px, 1fr) minmax(0, 2.4fr)",
                            },
                        }}
                    >
                        {launchError && (
                            <Alert
                                severity="warning"
                                sx={{ gridColumn: "1 / -1", width: "100%" }}
                            >
                                {launchError} Select another run below.
                            </Alert>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 3,
                            }}
                        >
                            <SectionCard sx={{ overflow: "visible", p: 2.5 }}>
                                <StepCardLabel>1 · Step to debug</StepCardLabel>
                                <Box sx={{ mt: 1.5 }}>
                                    <FlowStepSelect
                                        selectedStep={step}
                                        onChange={handleStepChange}
                                    />
                                </Box>
                            </SectionCard>

                            <SectionCard sx={{ p: 2.5 }}>
                                <StepCardLabel>2 · Input source</StepCardLabel>
                                <Box
                                    role="radiogroup"
                                    aria-label="Input source"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1.5,
                                        mt: 1.5,
                                    }}
                                >
                                    {INPUT_SOURCES.map((option) => (
                                        <InputSourceOption
                                            key={option.id}
                                            option={option}
                                            selected={inputSource === option.id}
                                            onSelect={() =>
                                                setInputSource(option.id)
                                            }
                                        />
                                    ))}
                                </Box>
                            </SectionCard>
                        </Box>

                        <SectionCard>
                            {inputSource === "run" ? (
                                <>
                                    {launchedRunId && !launchError ? (
                                        <SelectedRunSummary
                                            run={selectedRun}
                                            usersById={usersById}
                                        />
                                    ) : (
                                        <FlowStepDebugRunPicker
                                            step={step}
                                            service={service}
                                            users={users}
                                            filters={filters}
                                            onFiltersChange={setFilters}
                                            selectedRun={selectedRun}
                                            onSelectRun={handleSelectRun}
                                        />
                                    )}
                                    {requiresNode && selectedRun && (
                                        <FlowStepDebugNodePicker
                                            service={service}
                                            sourceRunId={selectedRun.id}
                                            sourceRunOutput={launchedRunOutput}
                                            selectedNodeId={selectedNodeId}
                                            debugTarget={
                                                selectedRun.id === launchedRunId
                                                    ? {
                                                          nodeId:
                                                              launchedState?.nodeId,
                                                          resultTitle:
                                                              launchedState?.resultTitle,
                                                      }
                                                    : undefined
                                            }
                                            onSelectNode={setSelectedNodeId}
                                        />
                                    )}
                                </>
                            ) : (
                                <FlowStepDebugManualInput
                                    step={step}
                                    value={manualInput}
                                    onChange={setManualInput}
                                />
                            )}
                            <SectionCardFooter
                                summary={selectionSummary}
                                action={
                                    <AcxButton
                                        color="primary"
                                        disabled={!configured}
                                        onClick={() =>
                                            setStage("Skill Instructions")
                                        }
                                    >
                                        Continue to skills
                                    </AcxButton>
                                }
                            />
                        </SectionCard>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FlowStepDebug;
